<template>
  <v-card class="pa-4">
    <v-row align="center">
      <v-col cols="6">
        <span class="font-weight-bold">
          {{ code }}
        </span>
      </v-col>

      <v-col cols="4">
        <order-status-label
          :status="status"
          :deleted-type="deletedType"></order-status-label>
      </v-col>

      <v-col
        cols="2"
        class="text-end">
        <v-btn
          text
          small
          color="primary"
          @click="toggleIsExpanded">
          <v-icon
            v-if="isExpanded"
            right>
            mdi-chevron-up
          </v-icon>
          <v-icon
            v-else
            right>
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="isExpanded">
      <v-row
        v-if="isRejectStatus && rejectReason"
        no-gutters>
        <v-col
          cols="12"
          class="pt-0"
        >
          <span class="error--text _fs-14">
            **{{ rejectReason }}
          </span>
        </v-col>
      </v-row>

      <v-row
        v-if="isDeletedStatus && !isCancelByUser"
        no-gutters>
        <v-col
          cols="12"
          class="pt-0"
        >
          <span class="error--text _fs-14">
            **{{ deleteReason || "รายการสั่งซื้อหมดอายุ เนื่องจากไม่มีการชำระภายใน 30 วันตามกำหนด" }}
          </span>
        </v-col>
      </v-row>

      <div class="mt-4">
        รายการที่ซื้อ
      </div>
      <ul>
        <li
          v-for="orderItem in orderItems"
          :key="orderItem._id">
          {{ orderItem.productName }}
        </li>
      </ul>

      <div class="mt-4">
        วันที่ทำการซื้อ: {{ createdAt | dateTimeFormat("DD MMM YYYY - HH:mm") }}
      </div>
    </template>

    <div
      v-if="isExpanded"
      class="mt-4 d-flex justify-space-around flex-wrap">
      <v-btn
        v-if="['new', 'pending', 'rejected'].includes(status)"
        class="ma-4"
        color="primary"
        rounded
        outlined
        min-width="220"
        :to="{ name: 'OrderEdit', params: { productType: type, id: orderId } }"
      >
        <v-icon left>
          mdi-pencil
        </v-icon>
        แก้ไขออเดอร์
      </v-btn>
      <v-btn
        v-if="['new', 'pending', 'rejected'].includes(status)"
        class="ma-4"
        color="error"
        rounded
        outlined
        min-width="220"
        @click="$emit('delete', orderId, code)"
      >
        <v-icon left>
          mdi-delete
        </v-icon>
        ลบออเดอร์
      </v-btn>
      <!-- แจ้งชำระเงิน when (new), (rejected, no withholding) -->
      <v-btn
        v-if="status === 'new' || (status === 'rejected' && !hasPaymentAttachment)"
        class="ma-4"
        color="primary"
        rounded
        min-width="220"
        :href="`/upload-slip/${orderId}`"
      >
        แจ้งชำระเงิน
      </v-btn>

      <!-- แนบใบหักภาษี ณ.ที่จ่าย when (pending, no withholding), (complete, no withholding) -->
      <v-btn
        v-if="isJuristicCustomer && ((status === 'pending' && !hasPaymentAttachment) || (status === 'complete' && !hasPaymentAttachment))"
        class="ma-4"
        color="primary"
        rounded
        min-width="220"
        @click="attachTaxDocument"
      >
        แนบใบภาษีหัก ณ ที่จ่าย
      </v-btn>

      <!-- ดูใบกำกับภาษี when (pending, has withholding), (complete, has withholding) -->
      <v-btn
        v-if="isJuristicCustomer && ((status === 'pending' && hasPaymentAttachment) || (status === 'complete' && hasPaymentAttachment))"
        class="ma-4"
        color="success"
        rounded
        min-width="220"
        :disabled="status === 'pending'"
        @click="viewTaxDocument"
      >
        <v-icon left>
          mdi-file-document
        </v-icon>
        ดูใบกำกับภาษี
      </v-btn>

      <v-btn
        class="ma-4"
        color="primary"
        rounded
        outlined
        min-width="220"
        :to="{ name: 'OrderDetail', params: { id: orderId } }"
      >
        <v-icon left>
          mdi-file
        </v-icon>
        ดูใบเสนอราคา
      </v-btn>
    </div>
    <template v-if="isNewLineOfficialAccount || isTransferLineOfficialAccount">
      <div class="mt-4">
        ประเภทลูกค้า
        <div class="primary--text font-weight-bold">
          {{ newLineOfficialAccountTypeText }}
        </div>
      </div>
      <template v-if="isTransferLineOfficialAccount">
        <div class="mt-4">
          Basic ID:
          <span class="primary--text font-weight-bold">
            {{ basicId }}
          </span>
        </div>
        <div class="mt-4">
          ช่องทางที่เคยชำระเงินมาก่อน
          <div class="primary--text font-weight-bold">
            {{ transferAgencyPaymentTypeText }}
          </div>
        </div>
      </template>
    </template>
    <div class="mt-4">
      สร้างโดย:
      <span class="font-weight-bold">
        {{ createdBy }}
      </span>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'MobileOrderCard',
  components: {
    OrderStatusLabel: () => import('./OrderStatusLabel')
  },
  props: {
    basicId: {
      type: String,
      default: ''
    },
    orderId: {
      type: String,
      default: ''
    },
    code: {
      type: String,
      default: ''
    },
    createdAt: {
      type: String,
      default: ''
    },
    orderItems: {
      type: Array,
      default: () => []
    },
    createdBy: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
    rejectReason: {
      type: String,
      default: ''
    },
    deleteReason: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    isNewLineOfficialAccount: {
      type: Boolean,
      default: false
    },
    isTransferLineOfficialAccount: {
      type: Boolean,
      default: false
    },
    newLineOfficialAccountTypeText: {
      type: String,
      default: ''
    },
    transferAgencyPaymentTypeText: {
      type: String,
      default: ''
    },
    isJuristicCustomer: {
      type: Boolean,
      default: false
    },
    hasPaymentAttachment: {
      type: Boolean,
      default: false
    },
    deletedType: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isExpanded: false
    }
  },
  computed: {
    isRejectStatus () {
      return this.status === 'rejected'
    },
    isDeletedStatus() {
      return this.status === 'deleted'
    },
    isTaxDocumentReady () {
      // For now, assume document is ready when status is 'complete'
      // This can be updated to check a specific field from backend
      return this.status === 'complete'
    },
    isCancelByUser () {
      return this.deletedType === 'userCancel'
    }
  },
  methods: {
    toggleIsExpanded () {
      this.isExpanded = !this.isExpanded
    },
    attachTaxDocument () {
      window.location.href = `/upload-withholding-document/${this.orderId}`
    },
    viewTaxDocument () {
      // Navigate to PEAK system for tax document viewing
      window.open(`/peak/tax-document/${this.orderId}`, '_blank')
    }
  }
}
</script>
